import { Box, Button, Card, Divider, Group, Modal, Select, Stack, Text } from "@mantine/core";
import { IUserDepricated } from "@mctypes/global.types";
import { IMeeting } from "@mctypes/meeting.types";
import { IExternalParticipant } from "@schemas/meetingSchema";
import { IconCircle, IconCircleCheck } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import React, { useState } from "react";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { Avatar, FailedAlert } from "../../../../../components";
import MemberCardDepricated from "../../../../../components/MemberCard/MemberCardDepricated";
import ViewEditTable from "../../../../../components/ViewEditTable/ViewEditTable";
import { useAppSelector } from "../../../../../hooks";
import { IMemberWithDomain } from "../../../admin/members/MembersTableDepricated";
import {
  useAddMeetingPartiipantMutation,
  useRemoveMeetingParticipantMutation,
  useUpdateExternalParticipantsMutation,
  useUpdateMeetingParticipantMutation,
} from "../../meetingRTK";

const Participants: React.FC<{ singleMeeting: IMeeting.MeetingPopulated }> = ({ singleMeeting }) => {
  const [selectedParticipant, setSelectedParticipant] = useState<IMeeting.ParticipantsPopulated>(null);

  const [addMeetingParticipant, { isError: addParticipantError }] = useAddMeetingPartiipantMutation();
  const [updateMeetingParticipant, { isError: updateParticipantError }] = useUpdateMeetingParticipantMutation();
  const [removeMeetingParticipant, { isError: removeParticipantError }] = useRemoveMeetingParticipantMutation();
  const [addExternalMeetingParticipant, { isError: addExternalParticipantError }] =
    useUpdateExternalParticipantsMutation();

  const isError =
    addParticipantError || updateParticipantError || removeParticipantError || addExternalParticipantError;

  const project = useAppSelector((state) => state.adminReducer.project);
  const participants = singleMeeting.participants;
  const externalParticipants = singleMeeting.externalParticipants;

  const newItemTemplateObject: {
    name: string;
    email: string;
    discipline: string;
    isPresent: boolean;
  } = {
    name: "",
    email: "",
    discipline: "",
    isPresent: false,
  };

  const columnsInternalParticipants: ColDef<any>[] = [
    {
      headerName: "",
      field: "user._id",
      maxWidth: 50,
      minWidth: 50,
      cellClass: "px-0",
      cellRenderer: (params) => (
        <Button
          color="red.5"
          variant="subtle"
          size="compact-sm"
          onClick={() =>
            removeMeetingParticipant({
              projectId: project._id,
              meetingId: singleMeeting._id,
              participantId: params.node.data._id,
            })
          }
        >
          <i className="fa fa-trash fa-fw" />
        </Button>
      ),
    },
    {
      field: "user.name",
      headerName: "Navn",
      flex: 1,
      cellRenderer: (params) => (
        <div className="d-flex align-items-center p-0 m-0">
          <div
            className="d-flex align-items-center pointer hoverText"
            onClick={() => setSelectedParticipant(params.node.data)}
          >
            {params.node.data.user && <Avatar id={params.node.data.user._id} name={params.node.data.user.name} />}
            {params.value}
          </div>
        </div>
      ),
    },
    {
      field: "discipline",
      headerName: "Disiplin",
      width: 100,
    },
    {
      width: 80,
      field: "isPresent",
      headerName: "Deltok",
      cellRenderer: (params) => (
        <Button
          size="md"
          variant="outline"
          px={"xs"}
          style={{ border: "none" }}
          c={params.value ? "green" : "lightgray.5"}
          aria-label={params.value ? "Merk som ikke deltatt" : "Merk som deltatt"}
          onClick={() =>
            updateMeetingParticipant({
              projectId: project._id,
              meetingId: singleMeeting._id,
              participantId: params.node.data._id,
              attribute: "isPresent",
              value: !params.value,
            })
          }
        >
          {params.value ? <IconCircleCheck scale={1.8} /> : <IconCircle scale={1.8} />}
        </Button>
      ),
    },
  ];

  const columnsExternalParticipants: ColDef<IExternalParticipant>[] = [
    {
      field: "name",
      headerName: "Navn",
      editable: true,
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      editable: true,
      flex: 1,
    },
    {
      field: "discipline",
      headerName: "Disiplin",
      editable: true,
      width: 100,
    },
    {
      width: 80,
      field: "isPresent",
      headerName: "Deltok",
      cellRenderer: (params) => (
        <Button
          size="md"
          variant="outline"
          px={"xs"}
          style={{ border: "none" }}
          c={params.value ? "green" : "lightgray.5"}
          aria-label={params.value ? "Merk som ikke deltatt" : "Merk som deltatt"}
          onClick={() => toggleIsPresentForExternalParticipant(params.node.data._id as string, !params.value)}
        >
          {params.value ? <IconCircleCheck scale={1.8} /> : <IconCircle scale={1.8} />}
        </Button>
      ),

      cellRendererParams: { editModeHidden: true },
    },
  ];

  const filteredMembers = (members, addedUsers) => {
    const filtered = members.filter((el1) => {
      return addedUsers.every((el2) => el1.user._id !== el2.user?._id);
    });
    return filtered;
  };

  const toggleIsPresentForExternalParticipant = (participantId: string, value: boolean) => {
    const updatedExternalParticipants = externalParticipants.map((participant) => {
      if (participant._id === participantId) {
        return {
          ...participant,
          isPresent: value,
        };
      }

      return participant;
    });

    onSaveExternalUsers(updatedExternalParticipants);
  };

  const onSaveExternalUsers = (externalParticipantsConverted: IExternalParticipant[]): IExternalParticipant[] => {
    addExternalMeetingParticipant({
      projectId: project._id,
      meetingId: singleMeeting._id,
      externalParticipants: externalParticipantsConverted,
    });

    return externalParticipantsConverted;
  };

  const defaultColDef = {
    suppressHeaderMenuButton: true,
    autoHeight: true,
  };

  return (
    <Stack w="100%" maw={900} p="sm" gap="sm">
      {isError && <FailedAlert />}
      <Modal
        opened={selectedParticipant != null}
        onClose={() => setSelectedParticipant(null)}
        title="Prosjektmedlem"
        size="lg"
        centered
      >
        {selectedParticipant && (
          <MemberCardDepricated
            member={(project.members as IMemberWithDomain[])?.find(
              (member) => (member.user as IUserDepricated)._id === selectedParticipant?.user?._id,
            )}
            projectId={project._id}
          />
        )}
      </Modal>
      <Card withBorder p="md">
        <Stack gap="sm">
          <Group align="flex-end" wrap="wrap" gap="md">
            <Box w={320}>
              <Select
                label="Legg til deltaker"
                placeholder="Søk etter deltaker"
                searchable
                value={undefined}
                data={filteredMembers(project?.members || [], participants).map((m) => ({
                  value: (m.user as IUserDepricated)._id,
                  label: (m.user as IUserDepricated).name,
                }))}
                onChange={(userId) => {
                  if (userId) {
                    addMeetingParticipant({ projectId: project._id, meetingId: singleMeeting._id, userId });
                  }
                }}
                maxDropdownHeight={300}
              />
            </Box>
          </Group>
          <Divider
            label={
              <Text fz="sm" fw={400} mb={3}>
                Prosjektdeltakere
              </Text>
            }
            labelPosition="left"
          />

          <Box className="ag-theme-alpine" id="myGrid">
            <AgGridReact
              domLayout="autoHeight"
              rowData={participants || []}
              getRowId={(p) => p.data._id}
              columnDefs={columnsInternalParticipants}
              defaultColDef={defaultColDef}
              animateRows={false}
              suppressContextMenu={true}
              localeText={AG_GRID_LOCALE_NOR}
              suppressRowVirtualisation={true}
            />
          </Box>
          <Divider
            label={
              <Text fz="sm" fw={400} mb={3}>
                Deltagere utenfor MCApps
              </Text>
            }
            labelPosition="left"
          />
          <ViewEditTable
            data={externalParticipants}
            columns={columnsExternalParticipants}
            onSave={onSaveExternalUsers}
            newItemTemplateObject={newItemTemplateObject}
            canEdit
          />
        </Stack>
      </Card>
    </Stack>
  );
};

export default Participants;
